import { useEffect, useState } from 'react'
import { toggleChecks } from '@/utils/acl'

import { IACL, OrgMemberRole, OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { CheckboxTableKey } from '@makyoapp/makyo-ui'

import { ACLValues, getOrgAcl, OrgACLLabel, rebuildOrgACL } from '@/adapters/ACLAdapter'
import { useUpdateACLMutation } from '@/resolvers/org/mutation'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgsContext } from '@/context/OrgsContext'
import { useReqError } from '@/hooks/useReqError'

/**
 * useACLOrg is a custom hook that handles the state and interactions for the ACL of an organization.
 * It allows for toggling the permissions for different roles in the organization, and it handles the updating of these permissions in the backend.
 *
 * @param {IACL} rules - The initial ACL rules.
 * @param {(saved: boolean) => void} setIsSaved - Function to set the `isSaved` state.
 *
 * @returns {Object} Returns an object with the following properties:
 * - `values`: The current ACL rules.
 * - `canEditACL`: A boolean value that determines if the current user can edit the ACL.
 * - `handleCheck`: A function that handles the change of a permission for a certain role.
 */
const useACLOrg = (rules: IACL, setIsSaved: (saved: boolean) => void) => {
  const printAPIError = useReqError()
  const { updateOrgsPerms, orgsPerms } = useOrgsContext()
  const [values, setValues] = useState<ACLValues<OrgACLLabel, OrgMemberRole>[]>(getOrgAcl(rules._Organization))
  const { mutate: updateAclMutation } = useUpdateACLMutation()

  // Getting the current organization from the context
  const { currentOrg } = useCurrentOrgContext()

  // Listen rules data to update checklist table
  useEffect(() => {
    setValues(getOrgAcl(rules._Organization))
  }, [rules])

  // Checking if the current user has permission to edit the ACL
  const canEditACL = orgsPerms[currentOrg?.id || '']?.[OrgPermission.ACLMakeChanges]

  // Function to toggle the scope of ACL
  const toggleScope = (nextValues: ACLValues<OrgACLLabel, OrgMemberRole>[]) => {
    // Setting the new values
    setValues(nextValues)
    // Rebuilding the organization ACL
    const orgACL = rebuildOrgACL(nextValues, rules._Organization)

    // Updating the ACL mutation
    updateAclMutation(
      { org: orgACL, team: rules._Team },
      {
        // On success, update the organization permissions and set isSaved to true
        onSuccess() {
          if (currentOrg) {
            updateOrgsPerms([currentOrg])
          }
          setIsSaved(true)
        },
        // On error, print the error
        onError(e) {
          printAPIError(e)
        },
      }
    )
  }

  // Function to handle the checking of a checkbox
  const handleCheck = (scopeIdx: number, check: string, key: CheckboxTableKey<OrgMemberRole>) => {
    // If the user does not have permission to edit the ACL, return immediately
    if (!canEditACL) {
      return
    }
    // Toggle the checks
    toggleChecks(scopeIdx, check, key.id, values, toggleScope)
  }

  return {
    values,
    canEditACL,
    handleCheck,
  }
}

export default useACLOrg
